import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import type { AppDispatch, RootState } from "store";
import { setPendingReferralCode } from "store/auth/authSlice";
import { persistReferralCodeWith, readReferralCodeWith } from "utils/referral";
import { useFetchReferralSettingsQuery } from "store/RTK/optionsApi";
import { trackEvent } from "utils/analytics";
import { clearReferralCodeWith } from "utils/referral";

const ReferralTracker: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const pendingReferral = useSelector(
    (state: RootState) => state.auth.pendingReferralCode,
  );
  const { data: settings } = useFetchReferralSettingsQuery();
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(location.search);
    const qp = settings?.attribution?.queryParam || "ref";
    const cookieName = settings?.attribution?.cookieName || "bs_referral";
    const referralParam = params.get(qp)?.trim() || null;

    if (referralParam) {
      if (!pendingReferral || pendingReferral !== referralParam) {
        persistReferralCodeWith(cookieName, referralParam);
        dispatch(setPendingReferralCode(referralParam));
      }

      // Remove only the configured param from URL
      params.delete(qp);
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash ?? ""}`;
      window.history.replaceState({}, "", newUrl);
      return;
    }

    if (!pendingReferral) {
      const cookieName = settings?.attribution?.cookieName || "bs_referral";
      const storedReferral = readReferralCodeWith(cookieName);
      if (storedReferral) {
        dispatch(setPendingReferralCode(storedReferral));
      }
    }

    // One-time attribution analytics after login/signup when cookie is present
    const cookieName2 = settings?.attribution?.cookieName || "bs_referral";
    if (isLoggedIn) {
      const flagKey = `referral_attributed_sent_${cookieName2}`;
      const already = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem(flagKey)
        : null;
      const stored = readReferralCodeWith(cookieName2);
      if (!already && stored) {
        trackEvent("referral.signup_attributed", { source: "cookie" });
        if (typeof window !== "undefined" && window.localStorage) {
          try {
            window.localStorage.setItem(flagKey, "1");
          } catch (storageError) {
            console.warn("referralTracker.storage_set_failed", storageError);
          }
        }
        clearReferralCodeWith(cookieName2);
      }
    }
  }, [location.search, location.pathname, dispatch, pendingReferral, settings?.attribution?.cookieName, settings?.attribution?.queryParam, isLoggedIn]);

  return null;
};

export default ReferralTracker;
